import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  Event,
  IEventPagination,
  Guest,
  IGuestPagination,
  GuestStatistics,
} from './@types';
import AuthServices from './auth.api';
import { getAuthToken } from '../utils/getAuthToken';

class EventService {
  private api: AxiosInstance;

  constructor(baseURL: string) {
    this.api = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.api.interceptors.request.use((config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          AuthServices.logout();
        }
        return Promise.reject(error);
      },
    );
  }

  public async getEvents(
    queryParams?: Record<string, any>,
  ): Promise<IEventPagination> {
    const response: AxiosResponse<IEventPagination> = await this.api.get(
      '/events',
      { params: queryParams },
    );
    return response.data;
  }

  public async getEvent(id: string): Promise<Event> {
    const response: AxiosResponse<Event> = await this.api.get(`/events/${id}`);
    return response.data;
  }

  public async getEventGuests(
    eventId: string,
    queryParams?: Record<string, any>,
  ): Promise<IGuestPagination> {
    const response: AxiosResponse<IGuestPagination> = await this.api.get(
      `/guests/event/${eventId}`,
      { params: queryParams },
    );
    return response.data;
  }

  public async getEventGuestStatistics(
    eventId: string,
  ): Promise<GuestStatistics> {
    const response: AxiosResponse<GuestStatistics> = await this.api.get(
      `/guests/event/${eventId}/statistics`,
    );
    return response.data;
  }
}

const _eventService = new EventService(
  process.env.REACT_APP_SERVER_API_URL || 'http://localhost:3000',
);

export default _eventService;
